import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useListFilters } from "@/hooks/use-list-filters";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useDeviceInstallations,
  type DeviceInstallationListParams,
} from "@/hooks/use-device-installations";
import { useDeviceInstallationsColumns } from "@/components/device-installations/device-installations-columns";
import { DEVICE_COLUMN_REGISTRY } from "@/components/device-installations/device-column-registry";
import { useEntityColumnRegistry } from "@/hooks/use-entity-column-registry";
import { RegistryLoadError } from "@/components/custom-fields/registry-load-error";
import { InstallationCreateDrawer } from "@/components/device-installations/installation-create-drawer";
import {
  DeviceInstallationsFilters,
  defaultDeviceFilters,
  type DeviceFilterState,
} from "@/components/device-installations/device-installations-filters";
import { useListColumnPreferences } from "@/hooks/use-list-column-preferences";
import { DeviceRowMenu, useDeviceRowMenu } from "@/components/device-installations/device-row-menu";
import { LetterCodeDialog } from "@/components/facilities/letter-code-dialog";
import { useLetterCodeDialog } from "@/components/facilities/use-letter-code-dialog";
import { ReplaceInstallationDrawer } from "@/components/device-installations/replace-installation-drawer";
import { TransferInstallationDrawer } from "@/components/device-installations/transfer-installation-drawer";
import { CreateViolationDrawer } from "@/components/violations/create-violation-drawer";
import { BulkEditEntryButton } from "@/components/bulk-edits/entry/bulk-edit-entry-button";
import { deviceListSelection } from "@/components/bulk-edits/entry/device-list-selection";
import { useSettledSelectionTotal } from "@/components/bulk-edits/entry/use-settled-selection-total";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  sortByForColumnId,
  visibleColumnCount as computeVisibleColumnCount,
} from "@/components/ui";

type DeviceInstallationListItem = components["schemas"]["DeviceInstallationListItem"];

export const Route = createFileRoute("/_authenticated/device-installations/")({
  component: DeviceInstallationsListPage,
});

const PAGE_SIZE = 20;

function DeviceInstallationsListPage() {
  const { t } = useTranslation(["installations", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can("device_installations", "create");
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  // This raw-DataTable page is the row context menu's SECOND mount — its own
  // instance of the one shared wiring (the facility tab's is the
  // DeviceInstallationsTable wrapper).
  const rowMenu = useDeviceRowMenu();
  const letterCodeDialog = useLetterCodeDialog();

  // Per-org persisted filter state — survives reloads and org switches via
  // localStorage["njbsoft-device-filters:<orgId>"].
  const { filters, setFilter, clearFilter, resetFilters } = useListFilters<DeviceFilterState>({
    storageKey: "njbsoft-device-filters",
    orgId,
    version: 2,
    defaults: defaultDeviceFilters,
  });
  const [offset, setOffset] = useState(0);
  // The org's custom-field definitions append as dynamic columns; the query's
  // success gates the table below (a pending/failed definitions query must
  // never reach the preference-save path).
  const {
    registry: deviceRegistry,
    defsByFieldKey,
    ready: registryReady,
    isError: registryFailed,
    refetch: refetchDefinitions,
  } = useEntityColumnRegistry("device_installation", DEVICE_COLUMN_REGISTRY);
  // Soonest-due-first: the list is the testing worklist.
  const [sorting, setSorting] = useState<SortingState>([{ id: "next_test_due_date", desc: false }]);

  const firstSort = sorting[0];
  // `sortByForColumnId` returns `string | undefined` while the params type is a
  // closed union. What licenses the cast is the registry test's "every registry
  // sort key is a member of the wire sort_by union" case, which checks
  // membership at runtime AND fails to compile if the wire union grows a key the
  // list does not know — that pair is the drift guard replacing the hand-
  // maintained SORT_KEYS set this page used to carry.
  const sortBy = firstSort
    ? (sortByForColumnId(deviceRegistry, firstSort.id) as DeviceInstallationListParams["sort_by"])
    : undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  const { visibility, setColumnVisibility, resetColumns } = useListColumnPreferences(
    "device_installations",
    deviceRegistry,
  );

  // Any filter or sort change resets pagination to the first page.
  useEffect(() => {
    setOffset(0);
  }, [sorting, filters]);

  const columns = useDeviceInstallationsColumns(deviceRegistry, defsByFieldKey, {
    onOpenRowMenu: rowMenu.openRowMenu,
  });
  const extraColumnCount = columns.length - deviceRegistry.length;
  const visibleColumnCount = computeVisibleColumnCount(
    deviceRegistry,
    visibility,
    extraColumnCount,
  );
  // The status dropdown picks one lifecycle value; the API takes a set. The
  // one params object feeds both the list read and the bulk edit selection, so
  // the button always describes the rows on screen.
  const { device_status: statusFilter, ...restFilters } = filters;
  const listParams: DeviceInstallationListParams = {
    ...restFilters,
    device_status: statusFilter ? [statusFilter] : undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: PAGE_SIZE,
    offset,
  };
  const { data, isLoading, isError, isPlaceholderData, refetch } =
    useDeviceInstallations(listParams);
  const selection = deviceListSelection(listParams);
  const selectionTotal = useSettledSelectionTotal(selection, {
    total: data?.total ?? 0,
    isPlaceholderData,
    isError,
  });

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleRowClick = (row: DeviceInstallationListItem) => {
    void navigate({
      to: "/device-installations/$id",
      params: { id: row.id },
    });
  };

  // Both create affordances open the shared create drawer — the ONE create
  // interface, gated at the open point on the caller's grant.
  const handleNewInstallation = () => {
    setCreateDrawerOpen(true);
  };

  if (!registryReady) {
    // Definitions unresolved: no table, no filters bar (it carries the Columns
    // picker), no preference-save path. Pending gets the standing skeleton; a
    // FAILED read gets the same error shape the list read uses, because a
    // skeleton that never resolves reads as a page still loading.
    return (
      <div className="p-8">
        <PageHeader title={t("installations:title")} subtitle={t("installations:subtitle")} />
        <div className="mt-6">
          {registryFailed ? (
            <RegistryLoadError onRetry={refetchDefinitions} />
          ) : (
            <SkeletonTable columns={visibleColumnCount} rows={5} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <PageHeader
        title={t("installations:title")}
        subtitle={t("installations:subtitle")}
        actions={
          <>
            <BulkEditEntryButton
              recordType="device_installations"
              translation={selection}
              total={selectionTotal}
            />
            {canCreate && (
              <Button onClick={handleNewInstallation}>{t("installations:newInstallation")}</Button>
            )}
          </>
        }
      />

      <div className="mt-6">
        <DeviceInstallationsFilters
          filters={filters}
          registry={deviceRegistry}
          setFilter={setFilter}
          clearFilter={clearFilter}
          resetFilters={resetFilters}
          columnVisibility={visibility}
          onColumnVisibilityChange={setColumnVisibility}
          onColumnReset={resetColumns}
        />
      </div>

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={visibleColumnCount} rows={5} />
        </div>
      ) : isError ? (
        // A failed list read must never render the authoritative "no
        // installed devices" empty state — on a compliance surface an error
        // presented as a clean record is a lie. Error REPLACES the table.
        <div role="alert" className="mt-6 rounded-md bg-danger/10 p-4">
          <p className="text-danger">{t("installations:errors.loadListFailed")}</p>
          <div className="mt-3">
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              {t("installations:detail.retry")}
            </Button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("installations:empty.title")}
            description={t("installations:empty.description")}
            actionLabel={canCreate ? t("installations:empty.cta") : undefined}
            onAction={handleNewInstallation}
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={items}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
              manualSorting
              columnVisibility={visibility}
              onColumnVisibilityChange={setColumnVisibility}
              onRowContextMenu={rowMenu.handleRowContextMenu}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}

      {canCreate && (
        <InstallationCreateDrawer
          open={createDrawerOpen}
          onClose={() => setCreateDrawerOpen(false)}
        />
      )}

      {/* The row menu and the drawers it opens. A drawer session is set
          atomically (subject + open together) and cleared on close, so an open
          drawer is never handed a different row — the drawers' reset-while-closed
          contract. */}
      <DeviceRowMenu
        open={rowMenu.menuRow !== null}
        anchorRef={rowMenu.anchorRef}
        anchorPoint={rowMenu.anchorPoint}
        onClose={rowMenu.closeMenu}
        row={rowMenu.menuRow}
        onOpenDrawer={rowMenu.openDrawer}
        onGenerateLetterCode={(row) =>
          letterCodeDialog.open({
            facilityId: row.facility_id,
            facilityName: row.facility_summary.name,
          })
        }
      />
      <LetterCodeDialog subject={letterCodeDialog.subject} onClose={letterCodeDialog.close} />
      {rowMenu.drawer?.kind === "replace" ? (
        <ReplaceInstallationDrawer
          open
          onClose={rowMenu.closeDrawer}
          subject={rowMenu.drawer.subject}
        />
      ) : null}
      {rowMenu.drawer?.kind === "transfer" ? (
        <TransferInstallationDrawer
          open
          onClose={rowMenu.closeDrawer}
          subject={rowMenu.drawer.subject}
        />
      ) : null}
      {/* A list-row subject always carries its premise id; the check narrows
          the subject type the lifecycle drawers share. */}
      {rowMenu.drawer?.kind === "violation" && rowMenu.drawer.subject.facility.id !== null ? (
        <CreateViolationDrawer
          open
          lockedFacilityId={rowMenu.drawer.subject.facility.id}
          lockedInstallationId={rowMenu.drawer.subject.installationId}
          onClose={rowMenu.closeDrawer}
        />
      ) : null}
    </div>
  );
}
